import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDeleteNews, useNewsList } from '@/hooks/useNews';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BulkSelectionToolbar } from '@/components/list/BulkSelectionToolbar';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { Edit, Plus, Search, Trash2 } from 'lucide-react';

export function NewsList() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);

  const { data, isLoading } = useNewsList({ page, limit: 10, search });
  const deleteBerita = useDeleteNews();
  const selection = useBulkSelection((data?.data || []).map((item) => item.id));

  const totalPage = useMemo(() => Math.max(1, Math.ceil((data?.total || 0) / 10)), [data?.total]);

  const submitDelete = async () => {
    if (!pendingDeleteIds.length) return;
    for (const id of pendingDeleteIds) {
      await deleteBerita.mutateAsync(id);
    }
    selection.clearSelection();
    setPendingDeleteIds([]);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Berita</h2>
          <p className="text-muted-foreground">Kelola konten berita untuk dashboard.</p>
        </div>
        <Link to="/news/create">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Tambah Berita
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari berita..." className="pl-8" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <BulkSelectionToolbar
            className="m-4 mb-0"
            selectedCount={selection.selectedCount}
            itemLabel="berita"
            onClear={selection.clearSelection}
            onDelete={() => setPendingDeleteIds(selection.selectedIds)}
            deleteDisabled={deleteBerita.isPending}
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox checked={selection.allSelected} onCheckedChange={(checked) => selection.toggleAll(Boolean(checked))} />
                </TableHead>
                <TableHead>Judul</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Penulis</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead className="w-28">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={6} className="py-8 text-center">Memuat...</TableCell></TableRow>
              ) : data?.data.length ? (
                data.data.map((item) => (
                  <TableRow key={item.id} className={selection.isSelected(item.id) ? 'bg-muted/30' : undefined}>
                    <TableCell>
                      <Checkbox checked={selection.isSelected(item.id)} onCheckedChange={(checked) => selection.toggleOne(item.id, Boolean(checked))} />
                    </TableCell>
                    <TableCell className="font-medium">{item.judul}</TableCell>
                    <TableCell>{item.category || '-'}</TableCell>
                    <TableCell>{item.penulis || '-'}</TableCell>
                    <TableCell>{item.tanggal || '-'}</TableCell>
                    <TableCell className="space-x-2">
                      <Link to={`/news/${item.id}/edit`}>
                        <Button variant="outline" size="icon">
                          <Edit className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button variant="destructive" size="icon" onClick={() => setPendingDeleteIds([item.id])}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={6} className="py-8 text-center">Belum ada berita.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Sebelumnya</Button>
        <span className="text-sm text-muted-foreground">Halaman {page} dari {totalPage}</span>
        <Button variant="outline" size="sm" disabled={page >= totalPage} onClick={() => setPage((p) => p + 1)}>Selanjutnya</Button>
      </div>

      <Dialog open={pendingDeleteIds.length > 0} onOpenChange={() => setPendingDeleteIds([])}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hapus Berita</DialogTitle>
            <DialogDescription>
              Tindakan ini tidak dapat dibatalkan. {pendingDeleteIds.length > 1 ? `${pendingDeleteIds.length} berita akan dihapus.` : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDeleteIds([])}>Batal</Button>
            <Button variant="destructive" onClick={submitDelete}>Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
